import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { ShopifyProductCard } from '@/components/ShopifyProductCard';
import { useShopifyProducts } from '@/hooks/useShopifyProducts';

interface MerchAlbum {
  id: string;
  name: string;
  description: string | null;
  cover_image_url: string | null;
}

interface MerchAlbumItem {
  id: string;
  image_url: string;
  title: string | null;
  product_id: string | null;
  shopify_handle: string | null;
}

interface LocalProduct {
  id: string;
  slug: string;
}

export function MerchAlbumsSection() {
  const [albums, setAlbums] = useState<MerchAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState<MerchAlbum | null>(null);
  const [items, setItems] = useState<MerchAlbumItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [localProducts, setLocalProducts] = useState<LocalProduct[]>([]);

  const { products: shopifyProducts } = useShopifyProducts(50);

  useEffect(() => {
    const fetchData = async () => {
      const [albumsRes, productsRes] = await Promise.all([
        supabase
          .from('merch_albums' as any)
          .select('id, name, description, cover_image_url')
          .eq('is_active', true)
          .order('created_at', { ascending: false }),
        supabase
          .from('products')
          .select('id, slug'),
      ]);
      setAlbums((albumsRes.data as any[]) || []);
      setLocalProducts((productsRes.data as LocalProduct[]) || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const openAlbum = async (album: MerchAlbum) => {
    setSelectedAlbum(album);
    setItemsLoading(true);
    const { data } = await supabase
      .from('merch_album_items' as any)
      .select('id, image_url, title, product_id, shopify_handle')
      .eq('album_id', album.id)
      .order('display_order', { ascending: true });
    setItems((data as any[]) || []);
    setItemsLoading(false);
  };

  // Build a map: local product_id → Shopify product, or match directly by shopify_handle
  const getShopifyProduct = (item: MerchAlbumItem) => {
    // Direct shopify_handle match (Shopify-only products)
    if (item.shopify_handle) {
      return shopifyProducts.find(sp => sp.node.handle === item.shopify_handle) || null;
    }
    // Local product_id → slug → Shopify handle match
    if (item.product_id) {
      const local = localProducts.find(p => p.id === item.product_id);
      if (!local) return null;
      return shopifyProducts.find(sp => sp.node.handle === local.slug) || null;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (albums.length === 0) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-2xl md:text-3xl italic">Merch Albums</h2>
        <p className="text-muted-foreground text-sm mt-1">Explore our curated collections</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
        {albums.map(album => (
          <button
            key={album.id}
            onClick={() => openAlbum(album)}
            className="card-racing group text-left overflow-hidden transition-transform hover:scale-[1.02]"
          >
            <div className="aspect-[3/4] bg-secondary relative overflow-hidden">
              {album.cover_image_url ? (
                <img loading="lazy" decoding="async"
                  src={album.cover_image_url}
                  alt={album.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <span className="text-4xl opacity-30">📸</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="font-semibold text-white text-sm md:text-base truncate">{album.name}</h3>
                {album.description && (
                  <p className="text-white/70 text-xs mt-0.5 truncate">{album.description}</p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Album Detail Dialog */}
      <Dialog open={!!selectedAlbum} onOpenChange={open => { if (!open) setSelectedAlbum(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          {selectedAlbum && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold">{selectedAlbum.name}</h2>
                {selectedAlbum.description && <p className="text-sm text-muted-foreground">{selectedAlbum.description}</p>}
              </div>
              {itemsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : items.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No images in this album yet</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {items.map(item => {
                    const shopifyProduct = getShopifyProduct(item);

                    if (shopifyProduct) {
                      // Render as a full ShopifyProductCard with cart/wishlist
                      return (
                        <ShopifyProductCard
                          key={item.id}
                          product={shopifyProduct}
                        />
                      );
                    }

                    // Non-linked image — show as gallery item
                    return (
                      <button
                        key={item.id}
                        onClick={() => setLightboxImage(item.image_url)}
                        className="relative group overflow-hidden rounded-lg"
                      >
                        <img loading="lazy" decoding="async"
                          src={item.image_url}
                          alt={item.title || ''}
                          className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {item.title && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                            <p className="text-xs text-white font-medium truncate">{item.title}</p>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox for non-linked images */}
      <Dialog open={!!lightboxImage} onOpenChange={open => { if (!open) setLightboxImage(null); }}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-none">
          {lightboxImage && (
            <div className="flex items-center justify-center p-2">
              <img loading="lazy" decoding="async" src={lightboxImage} alt="" className="max-w-full max-h-[85vh] object-contain" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
